/* CINKELIS+ cart invariants guard — site-wide.

   The membership product (handle: cinkelis-plus, €9.99/year) has two
   hard constraints. This guard enforces both no matter where in the
   theme the visitor is or how the cart got into a non-conforming state:

     1. Quantity is always 1.
        Membership is single-quantity by design (one tag, one billing
        period, one customer). Anything that pushes the line above 1 —
        the cart drawer / cart page +/- steppers, direct /cart/add.js
        with quantity > 1, a stale tab from a previous session, third-
        party scripts — gets clamped back down to exactly 1.

     2. Removed entirely on logout.
        The membership tags a customer record. A guest cart with the
        line in it can't reach checkout meaningfully — there's no
        customer to tag. If the visitor adds it logged-in and then
        signs out, this guard silently empties the line.

   Strategy: post-hoc enforcement via the theme's PubSub bus.
     - On page load — one /cart.js peek; remove or clamp as needed.
     - On every PUB_SUB_EVENTS.cartUpdate — re-peek and re-enforce.
   This is one extra network round-trip after a cart mutation, which is
   simpler and less fragile than wrapping window.fetch.

   Reads window.cnkCustomerLoggedIn (set inline by layout/theme.liquid
   right before this script tag) for the logged-in/out state.

   Loaded site-wide via layout/theme.liquid, after constants.js + pubsub.js
   so PUB_SUB_EVENTS / window.subscribe / window.publish are all defined
   by the time we run (defer order is preserved). */

(function () {
  'use strict';

  // Fixed by the runbook (Shopify product slug). Kept as a constant rather
  // than read from a per-page config so the guard works on pages that
  // don't render the landing section (PDP, cart, drawer, etc.).
  var MEMBERSHIP_HANDLE = 'cinkelis-plus';

  // Tag every publish from this script so neither this subscriber nor the
  // landing-page modal's subscriber loops on our own corrections.
  var SOURCE = 'cnk-plus-guard';

  function isOurSource(payload) {
    if (!payload || !payload.source) return false;
    // Match anything originating from CINKELIS+ scripts so multiple
    // co-operating modules don't trip each other into infinite re-checks.
    return payload.source.indexOf('cnk-plus-') === 0;
  }

  // After any /cart/change.js response, refresh the visible UI from the
  // sections it returned (drawer + icon bubble) and republish a cartUpdate
  // so other subscribers (icon counters, analytics) see the change.
  // Tagged with our SOURCE so we don't loop back into another peek.
  function applyCartResponse(response) {
    if (!response) return;

    var drawer = document.querySelector('cart-drawer');
    if (drawer && response.sections && typeof drawer.renderContents === 'function') {
      try { drawer.renderContents({ sections: response.sections }, false); } catch (_) {}
    }

    // The /cart page uses <cart-items>, not <cart-drawer>. If we're on
    // it, ask the theme's element to re-render. Most Dawn-derived themes
    // (incl. this one) expose renderContents on cart-items too.
    var cartItems = document.querySelector('cart-items');
    if (cartItems && response.sections && typeof cartItems.renderContents === 'function') {
      try { cartItems.renderContents(response); } catch (_) {}
    }

    try {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: SOURCE,
          cartData: response
        });
      }
    } catch (_) {}
  }

  // Force a single line key down to quantity 1. Asks /cart/change.js for
  // the cart-drawer + cart-icon-bubble sections in the same response so
  // the visible UI updates without a follow-up fetch.
  function clampToOne(lineKey) {
    if (!lineKey) return;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 1,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(applyCartResponse)
      .catch(function () { /* non-fatal */ });
  }

  // Remove the membership line entirely. Same /cart/change.js endpoint
  // with quantity 0 — Shopify treats that as a removal and returns the
  // updated cart with re-rendered sections.
  function removeFromCart(lineKey) {
    if (!lineKey) return;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(applyCartResponse)
      .catch(function () { /* non-fatal */ });
  }

  // Peek the cart and enforce membership invariants:
  //   - logged out + present  → remove
  //   - logged in  + qty > 1  → clamp to 1
  // There's only ever one line per variant, so we can return after the
  // first match.
  function enforceInvariants() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return;
        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          if (item.handle !== MEMBERSHIP_HANDLE) continue;
          if (window.cnkCustomerLoggedIn === false) {
            removeFromCart(item.key);
          } else if (item.quantity > 1) {
            clampToOne(item.key);
          }
          return;
        }
      })
      .catch(function () { /* non-fatal */ });
  }

  // First-paint pass — covers logout-then-redirect, stale tabs, and any
  // page where a previous session left the cart in a non-conforming state.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforceInvariants, { once: true });
  } else {
    enforceInvariants();
  }

  // Live watch — every cart mutation site-wide flows through cartUpdate.
  // We skip our own publishes (and the landing modal's) to avoid loops.
  try {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (payload) {
        if (isOurSource(payload)) return;
        enforceInvariants();
      });
    }
  } catch (_) { /* PubSub bus not available — non-fatal */ }
})();
